// src/pages/monitoring/TindakLanjutDetail.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Plus,
    Upload,
    Clock,
    Target,
    Users,
    FileText,
    TrendingUp,
    AlertTriangle,
    Calendar,
    Download,
    AlertCircle,
    UserCog,
    RotateCcw,
    ChevronDown,
    CalendarPlus,
    XCircle,
} from 'lucide-react';
import {
    Button,
    Card,
    Breadcrumb,
    Modal,
    Input,
    Textarea,
    ProgressBar,
    Select,
    Loading,
} from '../../components/ui';
import KpiCard from '../../components/monitoring/KpiCard';
import { monitoringService } from '../../services/monitoring';
import { PpeppRekomendasi, PpeppKpi, HambatanTindakLanjut } from '../../types';
import { laporanService } from '../../services/laporan';
import { useRole } from '../../hooks/useRole';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';

// Interface untuk riwayat perpanjangan
interface RiwayatPerpanjanganItem {
    tanggal: string;
    deadline_lama: string | null;
    deadline_baru: string | null;
    alasan: string | null;
    pengubah: string | null;
}

// ========== UNIFIED TIMELINE TYPES ==========
type UnifiedTimelineEventType =
    | 'progress'
    | 'ppepp_created'
    | 'ppepp_cancelled'
    | 'pic_change'
    | 'deadline_extended'
    | 'returned_to_rapim';

interface UnifiedTimelineEvent {
    type: UnifiedTimelineEventType;
    date: Date;
    ppeppIndex?: number;
    ppeppId?: number;
    ppeppStatus?: string;
    isPpeppAktif?: boolean;
    data: any;
}

// Konstanta jenis hambatan yang diperluas
const JENIS_HAMBATAN_OPTIONS = [
    { value: 'Anggaran belum turun', label: 'Anggaran belum turun' },
    { value: 'Anggaran tidak cukup', label: 'Anggaran tidak cukup' },
    { value: 'SDM tidak tersedia', label: 'SDM tidak tersedia' },
    {
        value: 'Ketergantungan eksternal',
        label: 'Ketergantungan eksternal (vendor, pengiriman, dll)',
    },
    { value: 'Kebijakan berubah', label: 'Kebijakan berubah' },
    { value: 'Vendor/Mitra bermasalah', label: 'Vendor/Mitra bermasalah' },
    { value: 'Lainnya', label: 'Lainnya' },
];

// Timeline item type
type TimelineItemType = 'progress' | 'change' | 'perpanjang' | 'dikembalikan';

interface TimelineItem {
    type: TimelineItemType;
    date: Date;
    data: any;
}

const TindakLanjutDetail: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const role = useRole();
    const { user } = useAuthStore();

    // Core data state
    const [data, setData] = useState<PpeppRekomendasi | null>(null);
    const [loading, setLoading] = useState(true);
    const [kpis, setKpis] = useState<PpeppKpi[]>([]);

    // Progress form state
    const [showAddProgress, setShowAddProgress] = useState(false);
    const [newProgress, setNewProgress] = useState({
        progress_percentage: '',
        keterangan: '',
        bukti: null as File | null,
    });
    const [saving, setSaving] = useState(false);

    // Hambatan form state
    const [showHambatanForm, setShowHambatanForm] = useState(false);
    const [savingHambatan, setSavingHambatan] = useState(false);
    const [hambatanForm, setHambatanForm] = useState({
        jenis_hambatan: '',
        deskripsi: '',
        usulan_solusi: '',
    });

    // Extend deadline state
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [newDeadline, setNewDeadline] = useState('');
    const [alasanPerpanjang, setAlasanPerpanjang] = useState('');

    // Change PIC state
    const [showChangePicModal, setShowChangePicModal] = useState(false);
    const [picCandidates, setPicCandidates] = useState<
        Array<{
            id: number;
            name: string;
            email: string;
            unit_id: number;
            unit?: { id: number; name: string };
        }>
    >([]);
    const [loadingPicCandidates, setLoadingPicCandidates] = useState(false);
    const [selectedPic, setSelectedPic] = useState('');
    const [alasanGantiPic, setAlasanGantiPic] = useState('');
    const [filterUnitPic, setFilterUnitPic] = useState('');

    // Return to RAPIM state
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [alasanKembaliRapim, setAlasanKembaliRapim] = useState('');

    // Action loading state
    const [actionLoading, setActionLoading] = useState(false);
    const [submittingKonfirmasiSelesai, setSubmittingKonfirmasiSelesai] =
        useState(false);

    // ========== DATA DERIVATIONS (HOOKS) ==========
    const progressHistory = useMemo(() => {
        if (!data?.progress) return [];
        return [...data.progress].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
    }, [data?.progress]);
    const laporanTerkait = (data as any)?.laporan_masalah || (data as any)?.laporan || {};
    const statusEfektif = (data as any)?.status_efektif || data?.status;
    const progressValue = Number(data?.progress_percentage || 0);

    

    // ========== FLAG: PERLU REVISI ==========
    const perluRevisi = useMemo(() => {
        if (!progressHistory || progressHistory.length === 0) return false;
        if (statusEfektif !== 'berlangsung') return false;
        if (progressValue < 100) return false;

        const lastProgress = progressHistory[progressHistory.length - 1];
        const keterangan = (lastProgress?.keterangan || '').toLowerCase();

        const rejectionKeywords = [
            'ditolak',
            'menolak',
            'dikembalikan',
            'revisi diperlukan',
            'belum dapat diterima',
            'tolak konfirmasi',
        ];

        return rejectionKeywords.some((keyword) => keterangan.includes(keyword));
    }, [progressHistory, statusEfektif, progressValue]);


    const picHistory = useMemo(() => {
        const history = (data as any)?.riwayat_pic || [];
        return [...history].sort((a: any, b: any) => {
            const ta = a?.tanggal ? new Date(a.tanggal).getTime() : 0;
            const tb = b?.tanggal ? new Date(b.tanggal).getTime() : 0;
            return ta - tb;
        });
    }, [(data as any)?.riwayat_pic]);

    const riwayatPerpanjangan = useMemo((): RiwayatPerpanjanganItem[] => {
        const history = (data as any)?.riwayat_perpanjangan || [];
        return [...history].sort((a: any, b: any) => {
            const ta = a?.tanggal ? new Date(a.tanggal).getTime() : 0;
            const tb = b?.tanggal ? new Date(b.tanggal).getTime() : 0;
            return ta - tb;
        });
    }, [(data as any)?.riwayat_perpanjangan]);

    const riwayatPpepp = useMemo(() => {
        const history = (data as any)?.riwayat_ppepp || [];
        return [...history].sort((a: any, b: any) => {
            const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
            const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
            if (ta === tb) return (a?.id || 0) - (b?.id || 0);
            return ta - tb;
        });
    }, [(data as any)?.riwayat_ppepp]);

    const unifiedTimeline = useMemo((): UnifiedTimelineEvent[] => {
        if (!data) return [];
        const events: UnifiedTimelineEvent[] = [];

        // 1. PPEPP Events (Created, Progress, Returned)
        riwayatPpepp.forEach((ppepp: any, index: number) => {
            const ppeppIndex = index + 1;
            const isPpeppAktif = ppepp.id === data.id;

            // Event pembuatan PPEPP
            events.push({
                type: 'ppepp_created',
                date: new Date(ppepp.created_at),
                ppeppIndex,
                ppeppId: ppepp.id,
                ppeppStatus: ppepp.status,
                isPpeppAktif,
                data: {
                    program: ppepp.program,
                    pic_user: ppepp.pic_user || ppepp.picUser,
                    pic_unit: ppepp.pic_unit || ppepp.picUnit,
                    rapat: ppepp.rapat_pimpinan,
                    target_date: ppepp.target_date,
                },
            });

            // Progress updates dari PPEPP ini
            if (ppepp.progress && Array.isArray(ppepp.progress)) {
                ppepp.progress.forEach((prog: any) => {
                    events.push({
                        type: 'progress',
                        date: new Date(prog.created_at),
                        ppeppIndex,
                        ppeppId: ppepp.id,
                        ppeppStatus: ppepp.status,
                        isPpeppAktif,
                        data: {
                            ...prog,
                            pic_at_time: prog.pic_user || ppepp.pic_user || ppepp.picUser || prog.updated_by,
                        },
                    });
                });
            }

            // Event pembatalan/dikembalikan ke RAPIM
            const ppeppStatus = String(ppepp.status).toLowerCase();
            if (ppeppStatus === 'dibatalkan' || ppeppStatus === 'cancelled') {
                const hKembali = ppepp.hambatan_tindak_lanjut?.find(
                    (h: any) => h.status === 'dikembalikan_rapim'
                );

                if (hKembali) {
                    events.push({
                        type: 'returned_to_rapim',
                        date: new Date(hKembali.created_at),
                        ppeppIndex,
                        ppeppId: ppepp.id,
                        ppeppStatus: ppepp.status,
                        isPpeppAktif: false,
                        data: {
                            catatan: hKembali.catatan_keputusan,
                            alasan: ppepp.alasan_dikembalikan,
                        },
                    });

                    events.push({
                        type: 'ppepp_cancelled',
                        date: new Date(hKembali.created_at),
                        ppeppIndex,
                        ppeppId: ppepp.id,
                        ppeppStatus: ppepp.status,
                        isPpeppAktif: false,
                        data: {
                            progress_terakhir: ppepp.progress_percentage || 0,
                        },
                    });
                }
            }
        });

        // 2. Ganti PIC events
        picHistory.forEach((change: any) => {
            events.push({
                type: 'pic_change',
                date: new Date(change.tanggal),
                data: change,
            });
        });

        // 3. Perpanjangan deadline
        riwayatPerpanjangan.forEach((ext: any) => {
            events.push({
                type: 'deadline_extended',
                date: new Date(ext.tanggal),
                data: ext,
            });
        });

        // Sort chronologically
        return events.sort((a, b) => a.date.getTime() - b.date.getTime());
    }, [riwayatPpepp, picHistory, riwayatPerpanjangan, data?.id]);

    // Tolak selesai state
    const [showTolakSelesaiModal, setShowTolakSelesaiModal] = useState(false);
    const [alasanTolakSelesai, setAlasanTolakSelesai] = useState('');

    // Tab state
    const [activeTab, setActiveTab] = useState<
        'detail' | 'riwayat_progres' | 'riwayat_ppepp' | 'hambatan'
    >('detail');
    const [expandedPpeppIds, setExpandedPpeppIds] = useState<Set<number>>(
        new Set()
    );

    // Fetch data on mount
    useEffect(() => {
        if (id) {
            fetchData();
        }
    }, [id]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const res = await monitoringService.getTindakLanjutDetail(Number(id));
            setData(res);
            setNewProgress((prev) => ({
                ...prev,
                progress_percentage: res.progress_percentage?.toString() || '',
            }));
            setKpis(res.kpis || []);
        } catch (error) {
            console.error('Error fetching detail:', error);
            toast.error('Gagal memuat detail tindak lanjut');
        } finally {
            setLoading(false);
        }
    };

    // Handler: Add Progress
    const handleAddProgress = async () => {
        if (!newProgress.keterangan.trim()) {
            toast.error('Keterangan wajib diisi');
            return;
        }
        if (!newProgress.progress_percentage) {
            toast.error('Persentase progress wajib diisi');
            return;
        }

        setSaving(true);
        try {
            await monitoringService.addProgress(Number(id), {
                progress_percentage: Number(newProgress.progress_percentage),
                keterangan: newProgress.keterangan,
                bukti: newProgress.bukti || undefined,
            });
            toast.success('Progress berhasil ditambahkan');
            setShowAddProgress(false);
            setNewProgress({ progress_percentage: '', keterangan: '', bukti: null });
            fetchData();
        } catch (e: any) {
            const errorMsg =
                e?.response?.data?.message || e?.message || 'Gagal menambahkan progress';
            if (e?.response?.status >= 500) {
                toast.error(
                    'Server error, tapi data mungkin sudah tersimpan. Silakan refresh halaman.'
                );
            } else {
                toast.error(errorMsg);
            }
        } finally {
            setSaving(false);
        }
    };

    // Handler: Load PIC Candidates
    const loadPicCandidates = async () => {
        if (!data) return;
        try {
            setLoadingPicCandidates(true);
            const res = await monitoringService.getPicCandidates();
            setPicCandidates(res);
        } catch (e) {
            console.error('Error loading PIC candidates:', e);
            toast.error('Gagal memuat kandidat PIC');
        } finally {
            setLoadingPicCandidates(false);
        }
    };

    // Handler: Report Hambatan
    const handleReportHambatan = async () => {
        if (!hambatanForm.jenis_hambatan) {
            toast.error('Jenis hambatan wajib dipilih');
            return;
        }
        if (!hambatanForm.deskripsi.trim()) {
            toast.error('Deskripsi hambatan wajib diisi');
            return;
        }

        setSavingHambatan(true);
        try {
            await monitoringService.reportHambatan(Number(id), {
                jenis_hambatan: hambatanForm.jenis_hambatan,
                deskripsi: hambatanForm.deskripsi,
                usulan_solusi: hambatanForm.usulan_solusi || undefined,
            });
            toast.success('Hambatan berhasil dilaporkan');
            setShowHambatanForm(false);
            setHambatanForm({ jenis_hambatan: '', deskripsi: '', usulan_solusi: '' });
            fetchData();
        } catch (e: any) {
            const msg = e?.response?.data?.message || 'Gagal melaporkan hambatan';
            toast.error(msg);
        } finally {
            setSavingHambatan(false);
        }
    };

    // Handler: Perpanjang Deadline
    const handlePerpanjangDeadline = async () => {
        if (!newDeadline) {
            toast.error('Tanggal baru wajib diisi');
            return;
        }
        if (!alasanPerpanjang.trim()) {
            toast.error('Alasan perpanjangan wajib diisi');
            return;
        }
        setActionLoading(true);
        try {
            await monitoringService.perpanjangDeadline(
                Number(id),
                newDeadline,
                alasanPerpanjang
            );
            toast.success('Deadline berhasil diperpanjang');
            setShowExtendModal(false);
            setNewDeadline('');
            setAlasanPerpanjang('');
            fetchData();
        } catch (e: any) {
            const msg = e?.response?.data?.message || 'Gagal memperpanjang deadline';
            toast.error(msg);
        } finally {
            setActionLoading(false);
        }
    };

    // Handler: Ganti PIC
    const handleGantiPic = async () => {
        if (!selectedPic) {
            toast.error('PIC baru wajib dipilih');
            return;
        }
        if (!alasanGantiPic.trim()) {
            toast.error('Alasan pergantian wajib diisi');
            return;
        }
        setActionLoading(true);
        try {
            await monitoringService.gantiPic(
                Number(id),
                Number(selectedPic),
                alasanGantiPic
            );
            toast.success('PIC berhasil diganti');
            setShowChangePicModal(false);
            setSelectedPic('');
            setAlasanGantiPic('');
            fetchData();
        } catch (e: any) {
            const msg = e?.response?.data?.message || 'Gagal mengganti PIC';
            toast.error(msg);
        } finally {
            setActionLoading(false);
        }
    };

    // Handler: Kembalikan ke RAPIM
    const handleKembalikanRapim = async () => {
        if (!alasanKembaliRapim.trim()) {
            toast.error('Alasan wajib diisi');
            return;
        }
        setActionLoading(true);
        try {
            await monitoringService.kembalikanKeRapim(Number(id), alasanKembaliRapim);
            toast.success('Laporan dikembalikan ke antrean RAPIM');
            setShowReturnModal(false);
            setAlasanKembaliRapim('');
            fetchData();
        } catch (e: any) {
            const msg = e?.response?.data?.message || 'Gagal mengembalikan ke RAPIM';
            toast.error(msg);
        } finally {
            setActionLoading(false);
        }
    };

    // Handler: Ajukan Konfirmasi Selesai
    const handleAjukanKonfirmasiSelesai = async () => {
        setSubmittingKonfirmasiSelesai(true);
        try {
            await monitoringService.ajukanKonfirmasiSelesai(Number(id));
            toast.success('Berhasil diajukan ke menunggu konfirmasi selesai');
            await fetchData();
        } catch (e: any) {
            const msg =
                e?.response?.data?.message || 'Gagal mengajukan konfirmasi selesai';
            toast.error(msg);
        } finally {
            setSubmittingKonfirmasiSelesai(false);
        }
    };

    // Handler: Tolak Selesai
    const handleTolakSelesai = async () => {
        if (!alasanTolakSelesai.trim()) {
            toast.error('Alasan penolakan wajib diisi');
            return;
        }
        setActionLoading(true);
        try {
            await monitoringService.tolakKonfirmasiSelesai(
                Number(id),
                alasanTolakSelesai
            );
            toast.success('Konfirmasi selesai ditolak dan dikembalikan ke PIC');
            setShowTolakSelesaiModal(false);
            setAlasanTolakSelesai('');
            fetchData();
        } catch (e: any) {
            const msg =
                e?.response?.data?.message || 'Gagal menolak konfirmasi selesai';
            toast.error(msg);
        } finally {
            setActionLoading(false);
        }
    };

    // Handler: Terima Selesai
    const handleTerimaSelesai = async () => {
        setActionLoading(true);
        try {
            // ✅ Panggil endpoint baru yang dedicated
            await monitoringService.terimaKonfirmasiSelesai(Number(id));
            toast.success('Pekerjaan dikonfirmasi selesai');
            await fetchData();
        } catch (e: any) {
            const msg = e?.response?.data?.message || 'Gagal menyetujui penyelesaian';
            toast.error(msg);
        } finally {
            setActionLoading(false);
        }
    };

    // Helper: Format Currency
    const formatCurrency = (value: number | null) => {
        if (value === null) return '-';
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
        }).format(value);
    };

    // Helper: Format Date
    const formatDate = (dateStr: string | null | undefined) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    // Helper: Format Date Short
    const formatDateShort = (dateStr: string | null | undefined) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    // Loading state
    if (loading) {
        return <Loading variant="page" message="Memuat detail program..." />;
    }

    // Not found state
    if (!data) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <h2 className="text-xl font-bold text-neutral-text-primary mb-2">
                    Data Tidak Ditemukan
                </h2>
                <Button onClick={() => navigate('/laporan')}>Kembali ke Laporan</Button>
            </div>
        );
    }

    // ========== COMPUTED VALUES ==========
    

    const targetDate = data.target_date ? new Date(data.target_date) : null;
    const targetDateOnly = targetDate
        ? new Date(
            targetDate.getFullYear(),
            targetDate.getMonth(),
            targetDate.getDate()
        )
        : null;
    const nowDateOnly = new Date();
    nowDateOnly.setHours(0, 0, 0, 0);
    const daysRemaining = targetDateOnly
        ? Math.ceil(
            (targetDateOnly.getTime() - nowDateOnly.getTime()) /
            (1000 * 60 * 60 * 24)
        )
        : 0;
    const tanggalSelesaiRaw =
        laporanTerkait.completed_at ||
        (data as any).completed_at ||
        (data as any).updated_at ||
        laporanTerkait.updated_at ||
        null;
    const tanggalSelesai = tanggalSelesaiRaw
        ? new Date(tanggalSelesaiRaw)
        : null;
    const tanggalSelesaiOnly = tanggalSelesai
        ? new Date(
            tanggalSelesai.getFullYear(),
            tanggalSelesai.getMonth(),
            tanggalSelesai.getDate()
        )
        : null;
    const isMenungguKonfirmasi = statusEfektif === 'validasi';
    const statusSelesai = ['selesai', 'diarsipkan'].includes(
        String(laporanTerkait.status || '')
    );
    const isOverdue =
        !!targetDateOnly && !statusSelesai && targetDateOnly < nowDateOnly;
    const hariTerlambatHistoris =
        statusSelesai &&
            targetDateOnly &&
            tanggalSelesaiOnly &&
            tanggalSelesaiOnly > targetDateOnly
            ? Math.ceil(
                (tanggalSelesaiOnly.getTime() - targetDateOnly.getTime()) /
                (1000 * 60 * 60 * 24)
            )
            : 0;
    const statusKetepatanSelesai = statusSelesai
        ? hariTerlambatHistoris > 0
            ? `Selesai terlambat ${hariTerlambatHistoris} hari`
            : 'Selesai tepat waktu'
        : '';

    // Validasi diperlonggar dari H-7 ke H-14
    const canReportHambatan =
        statusEfektif === 'berlangsung' &&
        progressValue < 100 &&
        !!data.target_date &&
        daysRemaining <= 14;

    const hambatanList: HambatanTindakLanjut[] =
        (data as any).hambatan_tindak_lanjut || (data as any).hambatan || [];
    const hambatanOrdered = hambatanList.slice().sort((a, b) => {
        const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
    });

    const isPimpinan = role.isRektor || role.isWarek || role.isAdmin;
    const isPicAktif = user?.id === data.pic_user_id;
    const jumlahPerpanjangan = Number(data.jumlah_perpanjangan || 0);
    const batasPerpanjanganTercapai = jumlahPerpanjangan >= 2;
    const statusTerhambat = statusEfektif === 'terhambat';
    const hambatanTerbaru = hambatanOrdered[0];
    const dikembalikanRapim =
        hambatanTerbaru?.status === 'dikembalikan_rapim' ||
        statusEfektif === 'dibatalkan';
    const ppeppAktifTerbaruId = Number(
        (data as any).ppepp_aktif_terbaru_id || 0
    );
    const isArsipPpeppLama =
        dikembalikanRapim &&
        ppeppAktifTerbaruId > 0 &&
        ppeppAktifTerbaruId !== data.id;
    const picUserTampil = (data as any).pic_user_tampil || data.pic_user;
    const picUnitTampil = (data as any).pic_unit_tampil || data.pic_unit;
    const hambatanKembali = hambatanOrdered.find(
        (item) => item?.status === 'dikembalikan_rapim'
    );
    const showHambatanActions =
        isPimpinan && statusTerhambat && !statusSelesai && !dikembalikanRapim;
    const canTandaiSelesai =
        isPicAktif &&
        statusEfektif === 'berlangsung' &&
        progressValue >= 100 &&
        !dikembalikanRapim &&
        !perluRevisi;
    const showHambatanCard = hambatanOrdered.length > 0 || statusTerhambat;

    // Riwayat PPEPP sorted chronologically
    const latestRiwayatPpeppId =
        riwayatPpepp.length > 0 ? riwayatPpepp[riwayatPpepp.length - 1].id : null;
    const ppeppAktifTerbaru =
        ppeppAktifTerbaruId > 0
            ? riwayatPpepp.find((item) => Number(item.id) === ppeppAktifTerbaruId) ||
            null
            : null;
    const latestPicChange =
        picHistory.length > 0 ? picHistory[picHistory.length - 1] : null;
    const isPicLama =
        !isPicAktif &&
        picHistory.some((item: any) => item?.pic_baru_id === user?.id);

    // Transfer info calculation
    const transferInfo = (() => {
        if (!user?.id || picHistory.length === 0) return null;
        const latestForUser = picHistory
            .filter((item: any) => item?.pic_baru_id && item.pic_baru_id === user.id)
            .sort((a: any, b: any) => {
                const ta = a?.tanggal ? new Date(a.tanggal).getTime() : 0;
                const tb = b?.tanggal ? new Date(b.tanggal).getTime() : 0;
                return tb - ta;
            })[0];

        if (!latestForUser) return null;

        const transferDate = latestForUser.tanggal
            ? new Date(latestForUser.tanggal)
            : null;
        const progressAtTransfer = transferDate
            ? progressHistory
                .filter((p) => new Date(p.created_at) <= transferDate)
                .slice(-1)[0]?.progress_percentage ??
            data.progress_percentage ??
            0
            : data.progress_percentage ?? 0;

        return {
            tanggal: latestForUser.tanggal,
            picLama: latestForUser.pic_lama,
            progressAtTransfer: Number(progressAtTransfer || 0),
        };
    })();

    // Helper to extract value from catatan keputusan
    const extractCatatanValue = (
        catatan: string | null | undefined,
        label: string
    ) => {
        if (!catatan) return '';
        const pattern = new RegExp(`${label}\\s*:\\s*([^\\.]+)`, 'i');
        const match = catatan.match(pattern);
        return match?.[1]?.trim() || '';
    };

    const alasanKembali = extractCatatanValue(
        hambatanKembali?.catatan_keputusan,
        'Alasan'
    );
    const diputuskanOleh = extractCatatanValue(
        hambatanKembali?.catatan_keputusan,
        'Diputuskan oleh'
    );

    // Status badge styling
    const statusBadgeLabel = isArsipPpeppLama
        ? 'Arsip (RAPIM Ulang Selesai)'
        : dikembalikanRapim
            ? 'Dikembalikan ke RAPIM'
            : statusEfektif === 'validasi'
                ? 'Menunggu Konfirmasi Selesai'
                : statusEfektif === 'berlangsung'
                    ? 'Dalam Proses'
                    : statusEfektif === 'selesai'
                        ? 'Selesai'
                        : statusEfektif === 'terhambat'
                            ? 'Terhambat'
                            : statusEfektif;

    const statusBadgeClass = isArsipPpeppLama
        ? 'bg-neutral-bg text-neutral-text-secondary'
        : dikembalikanRapim
            ? 'bg-danger/10 text-danger'
            : statusEfektif === 'validasi'
                ? 'bg-amber-100 text-amber-700'
                : statusEfektif === 'berlangsung'
                    ? 'bg-warning/10 text-warning'
                    : statusEfektif === 'selesai'
                        ? 'bg-success/10 text-success'
                        : statusEfektif === 'terhambat'
                            ? 'bg-danger/10 text-danger'
                            : 'bg-neutral-bg text-neutral-text-secondary';

    // Display values
    const sisaWaktuLabel = dikembalikanRapim
        ? '—'
        : !data.target_date
            ? '-'
            : daysRemaining < 0
                ? `${Math.abs(daysRemaining)} hari terlambat`
                : `${daysRemaining} hari`;

    const targetLabel = dikembalikanRapim
        ? '—'
        : data.target_date
            ? formatDate(data.target_date)
            : '-';

    const sisaWaktuLabelDisplay = dikembalikanRapim
        ? '—'
        : statusSelesai
            ? statusKetepatanSelesai
            : isMenungguKonfirmasi
                ? 'Menunggu Konfirmasi'
                : sisaWaktuLabel;

    const targetTitleDisplay = statusSelesai
        ? 'Tanggal Selesai Aktual'
        : 'Target';

    const targetLabelDisplay = dikembalikanRapim
        ? '—'
        : statusSelesai
            ? tanggalSelesai
                ? formatDate(tanggalSelesai.toISOString())
                : '-'
            : targetLabel;



    // ========== RENDER ==========
    return (
        <div className="space-y-6 animate-in">
            <Breadcrumb
                items={[
                    { label: 'Dashboard', href: '/dashboard' },
                    { label: 'Laporan', href: '/laporan' },
                    { label: 'Detail Tindak Lanjut' },
                ]}
            />

            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div>
                    <Link
                        to="/laporan"
                        className="inline-flex items-center gap-1 text-sm text-neutral-text-secondary hover:text-teal-main mb-2"
                    >
                        <ArrowLeft className="w-4 h-4" /> Kembali ke Laporan
                    </Link>
                    <h1 className="text-xl font-black text-teal-dark tracking-tight">
                        {(data as any).laporan_masalah?.judul ||
                            (data as any).laporan?.judul ||
                            'Detail Tindak Lanjut'}
                    </h1>
                    <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs font-mono text-teal-main bg-teal-main/5 px-2 py-0.5 rounded">
                            {(data as any).laporan_masalah?.kode ||
                                (data as any).laporan?.kode ||
                                '-'}
                        </span>
                        <span
                            className={`px-2 py-0.5 rounded-full text-xs font-bold ${statusBadgeClass}`}
                        >
                            {statusBadgeLabel}
                        </span>
                    </div>

                    {/* Transfer Info Banner */}
                    {transferInfo && (
                        <div className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                            Anda menerima alih tanggung jawab ini pada{' '}
                            {formatDate(transferInfo.tanggal)} dari{' '}
                            {transferInfo.picLama || '-'} yang telah mencapai progres{' '}
                            {transferInfo.progressAtTransfer}%.
                        </div>
                    )}
                    {/* Banner Revisi Diperlukan */}
                    {perluRevisi && (
                        <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="font-semibold">📝 Perlu Perbaikan</p>
                                <p className="mt-1">
                                    Pengajuan selesai ditolak. Lengkapi dokumen/bukti yang diminta, lalu ajukan kembali.
                                </p>
                            </div>
                        </div>
                    )}


                    {/* PIC Lama Banner */}
                    {isPicLama && (
                        <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            Tanggung jawab tindak lanjut ini sudah dialihkan ke{' '}
                            <strong>{data.pic_user?.name || '-'}</strong>. Anda hanya dapat
                            melihat detail tanpa melakukan aksi.
                        </div>
                    )}

                    {/* Dikembalikan ke RAPIM Banner */}
                    {dikembalikanRapim && !isArsipPpeppLama && (
                        <div className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                            Tindak lanjut ini sedang menunggu pembahasan ulang di RAPIM
                            berikutnya. PPEPP baru akan dibuat setelah rapat selesai.
                        </div>
                    )}

                    {/* Arsip PPEPP Banner */}
                    {isArsipPpeppLama && (
                        <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                            PPEPP ini adalah riwayat yang sudah diarsipkan. Keputusan RAPIM
                            ulang sudah menghasilkan PPEPP baru.
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-wrap">
                    {isArsipPpeppLama && (
                        <Button
                            variant="outline"
                            size="sm"
                            icon={<ArrowLeft className="w-4 h-4" />}
                            onClick={() => navigate(`/tindak-lanjut/${ppeppAktifTerbaruId}`)}
                        >
                            Buka PPEPP Aktif
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        icon={<Download className="w-4 h-4" />}
                        onClick={async () => {
                            try {
                                const arrayBuffer =
                                    await monitoringService.exportTindakLanjutPDF(Number(id));
                                const blob = new Blob([arrayBuffer], {
                                    type: 'application/pdf',
                                });
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', `Tindak-Lanjut-${id}.pdf`);
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                            } catch (e) {
                                console.error('Export PDF Error:', e);
                                toast.error('Gagal export PDF');
                            }
                        }}
                    >
                        Unduh PDF
                    </Button>
                    {isPicAktif &&
                        statusEfektif === 'berlangsung' &&
                        (progressValue < 100 || perluRevisi) &&
                        !dikembalikanRapim && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => setShowAddProgress(true)}
                                icon={<Plus className="w-4 h-4" />}
                            >
                                {perluRevisi ? 'Revisi & Update Progres' : 'Update Progres'}
                            </Button>
                        )}
                    {canTandaiSelesai && (
                        <Button
                            variant="success"
                            size="sm"
                            onClick={handleAjukanKonfirmasiSelesai}
                            loading={submittingKonfirmasiSelesai}
                        >
                            Ajukan Konfirmasi Selesai
                        </Button>
                    )}
                    {isMenungguKonfirmasi && isPimpinan && !dikembalikanRapim && (
                        <>
                            <Button
                                variant="outline"
                                size="sm"
                                className="text-danger border-danger/20 hover:bg-danger/10"
                                onClick={() => setShowTolakSelesaiModal(true)}
                            >
                                Tolak & Kembalikan
                            </Button>
                            <Button
                                variant="success"
                                size="sm"
                                onClick={handleTerimaSelesai}
                                loading={actionLoading}
                            >
                                Terima & Selesaikan
                            </Button>
                        </>
                    )}
                    {isPicAktif && canReportHambatan && !dikembalikanRapim && (
                        <Button
                            variant="warning"
                            size="sm"
                            onClick={() => {
                                setHambatanForm({
                                    jenis_hambatan: '',
                                    deskripsi: '',
                                    usulan_solusi: '',
                                });
                                setShowHambatanForm(true);
                            }}
                            icon={<AlertCircle className="w-4 h-4" />}
                        >
                            Laporkan Hambatan
                        </Button>
                    )}
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card padding="sm">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-teal-main/10">
                            <TrendingUp className="w-5 h-5 text-teal-main" />
                        </div>
                        <div>
                            <p className="text-xs text-neutral-text-secondary">Progress</p>
                            <p className="text-2xl font-black text-teal-main">
                                {data.progress_percentage || 0}%
                            </p>
                        </div>
                    </div>
                </Card>
                <Card padding="sm">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-warning/10">
                            <Clock className="w-5 h-5 text-warning" />
                        </div>
                        <div>
                            <p className="text-xs text-neutral-text-secondary">Sisa Waktu</p>
                            <p
                                className={`text-2xl font-black ${dikembalikanRapim
                                    ? 'text-neutral-text-secondary'
                                    : statusSelesai
                                        ? 'text-success'
                                        : daysRemaining < 0
                                            ? 'text-danger'
                                            : daysRemaining < 14
                                                ? 'text-warning'
                                                : 'text-neutral-text-primary'
                                    }`}
                            >
                                {sisaWaktuLabelDisplay}
                            </p>
                        </div>
                    </div>
                </Card>
                <Card padding="sm">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-info/10">
                            <Calendar className="w-5 h-5 text-info" />
                        </div>
                        <div>
                            <p className="text-xs text-neutral-text-secondary">
                                {targetTitleDisplay}
                            </p>
                            <p className="text-sm font-bold">{targetLabelDisplay}</p>
                        </div>
                    </div>
                </Card>
                <Card padding="sm">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-success/10">
                            <FileText className="w-5 h-5 text-success" />
                        </div>
                        <div>
                            <p className="text-xs text-neutral-text-secondary">Anggaran</p>
                            <p className="text-sm font-bold">
                                {formatCurrency(data.budget ? Number(data.budget) : null)}
                            </p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Progress Bar */}
            <Card>
                <h3 className="text-base font-bold text-teal-dark mb-3">
                    Progress Keseluruhan
                </h3>
                <ProgressBar
                    value={data.progress_percentage || 0}
                    size="lg"
                    color="teal"
                />
            </Card>

            {/* Tab Navigation */}
            <div className="flex items-center gap-1 border-b border-neutral-border">
                {[
                    { id: 'detail' as const, label: 'Detail' },
                    { id: 'riwayat_progres' as const, label: 'Riwayat Progres' },
                    { id: 'riwayat_ppepp' as const, label: 'Riwayat PPEPP' },
                    { id: 'hambatan' as const, label: 'Hambatan' },
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                            ? 'border-teal-main text-teal-main'
                            : 'border-transparent text-neutral-text-secondary hover:text-neutral-text-primary'
                            }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="min-h-[300px]">
                {/* Detail Tab */}
                {activeTab === 'detail' && (
                    <Card>
                        <h3 className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-4">
                            Detail Keputusan PPEPP
                        </h3>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2 space-y-4">
                                {[
                                    {
                                        label: 'Program',
                                        value: data.program,
                                        color: 'border-l-teal-main',
                                    },
                                    {
                                        label: 'Pengendalian',
                                        value: data.pengendalian,
                                        color: 'border-l-info',
                                    },
                                    {
                                        label: 'Evaluasi',
                                        value: data.evaluasi,
                                        color: 'border-l-warning',
                                    },
                                    {
                                        label: 'Pengembangan',
                                        value: data.pengembangan,
                                        color: 'border-l-success',
                                    },
                                    {
                                        label: 'Pelaporan',
                                        value: data.pelaporan,
                                        color: 'border-l-purple-500',
                                    },
                                ].map((item, i) => (
                                    <div key={i} className={`border-l-4 ${item.color} pl-3`}>
                                        <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary mb-1">
                                            {item.label}
                                        </p>
                                        <p className="text-xs text-neutral-text-primary leading-relaxed">
                                            {item.value || '-'}
                                        </p>
                                    </div>
                                ))}
                            </div>
                            <div className="space-y-4 lg:border-l lg:border-neutral-border lg:pl-6">
                                <h4 className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                                    Informasi PIC
                                </h4>
                                <div className="flex items-start gap-3">
                                    <Users className="w-4 h-4 text-neutral-text-secondary mt-0.5" />
                                    <div>
                                        <p className="text-xs text-neutral-text-secondary">
                                            Penanggung Jawab
                                        </p>
                                        <p className="text-sm font-medium">
                                            {picUserTampil?.name || '-'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Target className="w-4 h-4 text-neutral-text-secondary mt-0.5" />
                                    <div>
                                        <p className="text-xs text-neutral-text-secondary">Unit</p>
                                        <p className="text-sm font-medium">
                                            {picUnitTampil?.name || '-'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                )}

                {/* Riwayat Progres Tab */}
                {activeTab === 'riwayat_progres' && (
                    <Card>
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-base font-bold text-teal-dark">Riwayat Progress Lengkap</h3>
                                <p className="text-xs text-neutral-text-secondary mt-1">
                                    Semua event dari awal hingga kondisi saat ini, termasuk riwayat RAPIM ulang
                                </p>
                            </div>
                            <span className="text-xs text-neutral-text-secondary px-3 py-1 bg-neutral-bg rounded-full border border-neutral-border">
                                {unifiedTimeline.length} event
                            </span>
                        </div>

                        <div className="relative">
                            {/* Timeline vertical line */}
                            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-neutral-border" />

                            <div className="space-y-6">
                                {unifiedTimeline.length > 0 ? (
                                    unifiedTimeline.map((item, i) => {
                                        const isLatest = i === unifiedTimeline.length - 1;

                                        // ========== PPEPP CREATED ==========
                                        if (item.type === 'ppepp_created') {
                                            return (
                                                <div key={`ppepp-created-${i}`} className="relative pl-10">
                                                    <div className={`absolute left-2.5 w-4 h-4 rounded-full flex items-center justify-center text-white ${item.isPpeppAktif
                                                        ? 'bg-emerald-500 ring-2 ring-offset-2 ring-emerald-300'
                                                        : 'bg-slate-400'
                                                        }`}>
                                                        <Calendar className="w-2.5 h-2.5" />
                                                    </div>
                                                    <div className={`p-4 rounded-xl border ${item.isPpeppAktif
                                                        ? 'bg-emerald-50 border-emerald-200'
                                                        : 'bg-slate-50 border-slate-200'
                                                        }`}>
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <span className={`text-sm font-bold ${item.isPpeppAktif ? 'text-emerald-700' : 'text-slate-700'
                                                                }`}>
                                                                📋 PPEPP ke-{item.ppeppIndex} {item.isPpeppAktif ? '(Aktif Saat Ini)' : '(Historis)'}
                                                            </span>
                                                        </div>
                                                        <div className="text-xs space-y-1">
                                                            <div className="flex items-center gap-2">
                                                                <Users className="w-3 h-3 text-slate-600" />
                                                                <span className="font-semibold">PIC:</span>
                                                                <span>{item.data.pic_user?.name || item.data.pic_unit?.name || '-'}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <Target className="w-3 h-3 text-slate-600" />
                                                                <span className="font-semibold">Target:</span>
                                                                <span>{item.data.target_date ? formatDate(item.data.target_date) : '-'}</span>
                                                            </div>
                                                            {item.data.rapat && (
                                                                <div className="flex items-center gap-2">
                                                                    <Calendar className="w-3 h-3 text-slate-600" />
                                                                    <span className="font-semibold">Rapat:</span>
                                                                    <span>{item.data.rapat.judul}</span>
                                                                </div>
                                                            )}
                                                            <div className="mt-2 text-[11px] text-slate-600">
                                                                {formatDateShort(item.date.toISOString())}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        // ========== PROGRESS UPDATE ==========
                                        if (item.type === 'progress') {
                                            const entry = item.data;
                                            const keteranganRaw = entry.keterangan || '';

                                            // Deteksi jika ini adalah catatan penolakan
                                            const isRejectionNote = keteranganRaw.toLowerCase().includes('menolak') ||
                                                keteranganRaw.toLowerCase().includes('ditolak') ||
                                                keteranganRaw.toLowerCase().includes('dikembalikan');

                                            // Format keterangan agar lebih rapi
                                            const formatKeterangan = (text: string) => {
                                                // Jika terlalu panjang dan adalah penolakan, parse dan format ulang
                                                if (isRejectionNote && text.includes('dengan alasan:')) {
                                                    const parts = text.split('dengan alasan:');
                                                    const alasan = parts[1]?.split('.')[0]?.trim() || '';
                                                    return {
                                                        type: 'rejection',
                                                        alasan: alasan,
                                                        note: 'Dikembalikan untuk perbaikan'
                                                    };
                                                }
                                                return { type: 'normal', text };
                                            };

                                            const formattedKeterangan = formatKeterangan(keteranganRaw);

                                            return (
                                                <div key={`progress-${i}`} className="relative pl-10">
                                                    <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 border-white ${isRejectionNote
                                                        ? 'bg-amber-500'
                                                        : isLatest
                                                            ? 'bg-teal-main'
                                                            : 'bg-neutral-border'
                                                        }`} />
                                                    <div className={`p-4 rounded-xl ${isRejectionNote
                                                        ? 'bg-amber-50 border border-amber-200'
                                                        : isLatest
                                                            ? 'bg-teal-main/5 border border-teal-main/20'
                                                            : item.isPpeppAktif
                                                                ? 'bg-white border border-neutral-border'
                                                                : 'bg-slate-50 border border-slate-200'
                                                        }`}>
                                                        {/* Header: Progress Badge + Tanggal + PIC */}
                                                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                                            <div className="flex items-center gap-2">
                                                                {/* Badge Progress */}
                                                                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${isRejectionNote
                                                                    ? 'bg-amber-200 text-amber-800'
                                                                    : item.isPpeppAktif
                                                                        ? 'bg-teal-main/10 text-teal-main'
                                                                        : 'bg-slate-200 text-slate-700'
                                                                    }`}>
                                                                    {entry.progress_percentage}%
                                                                </span>

                                                                {/* Separator */}
                                                                <span className="text-neutral-border">•</span>

                                                                {/* Tanggal */}
                                                                <span className="text-xs text-neutral-text-secondary font-medium">
                                                                    {formatDateShort(entry.created_at)}
                                                                </span>

                                                                {/* Badge PPEPP Historis */}
                                                                {!item.isPpeppAktif && (
                                                                    <>
                                                                        <span className="text-neutral-border">•</span>
                                                                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
                                                                            PPEPP #{item.ppeppIndex}
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </div>

                                                            {/* PIC Info */}
                                                            <div className="flex items-center gap-1.5 text-xs text-neutral-text-secondary">
                                                                <Users className="w-3 h-3" />
                                                                <span>{entry.pic_at_time?.name || entry.updated_by?.name || '-'}</span>
                                                            </div>
                                                        </div>

                                                        {/* Content */}
                                                        {formattedKeterangan.type === 'rejection' ? (
                                                            <div className="space-y-2">
                                                                <div className="flex items-start gap-2">
                                                                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                                                    <div>
                                                                        <p className="text-sm font-semibold text-amber-800">
                                                                            Pengajuan Selesai Ditolak
                                                                        </p>
                                                                        <p className="text-sm text-amber-700 mt-1">
                                                                            <span className="font-medium">Alasan:</span> {formattedKeterangan.alasan}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <p className="text-xs text-amber-600 italic pl-6">
                                                                    {formattedKeterangan.note}
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-neutral-text-primary leading-relaxed">
                                                                {formattedKeterangan.text}
                                                            </p>
                                                        )}

                                                        {/* Bukti Link */}
                                                        {entry.bukti_url && (
                                                            <div className="mt-3 pt-2 border-t border-neutral-border/50 flex items-center gap-2">
                                                                <FileText className="w-3.5 h-3.5 text-teal-main" />
                                                                <a
                                                                    href={entry.bukti_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-xs text-teal-main font-medium hover:underline"
                                                                >
                                                                    📎 Lihat Bukti Lampiran
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        }

                                        // ========== RETURNED TO RAPIM ==========
                                        if (item.type === 'returned_to_rapim') {
                                            return (
                                                <div key={`returned-${i}`} className="relative pl-10">
                                                    <div className="absolute left-2.5 w-3 h-3 rounded-full border-2 border-white bg-red-500" />
                                                    <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <RotateCcw className="w-4 h-4 text-red-600" />
                                                            <span className="text-sm font-semibold text-red-700">
                                                                🔄 Dikembalikan ke RAPIM (PPEPP ke-{item.ppeppIndex})
                                                            </span>
                                                        </div>
                                                        <div className="text-xs text-red-800 space-y-1">
                                                            <div>Tanggal: {formatDateShort(item.date.toISOString())}</div>
                                                            {item.data.alasan && <div>Alasan: {item.data.alasan}</div>}
                                                            {item.data.catatan && (
                                                                <div className="mt-2 p-2 bg-white/50 rounded border border-red-200">
                                                                    {item.data.catatan}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        // ========== PPEPP CANCELLED ==========
                                        if (item.type === 'ppepp_cancelled') {
                                            return (
                                                <div key={`cancelled-${i}`} className="relative pl-10">
                                                    <div className="absolute left-2.5 w-3 h-3 rounded-full border-2 border-white bg-slate-400" />
                                                    <div className="p-3 rounded-xl bg-slate-100 border border-slate-300">
                                                        <div className="flex items-center gap-2 text-xs text-slate-700">
                                                            <XCircle className="w-3.5 h-3.5" />
                                                            <span className="font-semibold">
                                                                PPEPP ke-{item.ppeppIndex} diarsipkan (Progress terakhir: {item.data.progress_terakhir}%)
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        // ========== PIC CHANGE ==========
                                        if (item.type === 'pic_change') {
                                            const change = item.data;
                                            return (
                                                <div key={`pic-change-${i}`} className="relative pl-10">
                                                    <div className="absolute left-2.5 w-3 h-3 rounded-full border-2 border-white bg-blue-500" />
                                                    <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <UserCog className="w-4 h-4 text-blue-600" />
                                                            <span className="text-sm font-semibold text-blue-700">
                                                                PIC Dialihkan
                                                            </span>
                                                        </div>
                                                        <div className="text-sm text-blue-800 font-medium">
                                                            {change.pic_lama || '-'} → {change.pic_baru || '-'}
                                                        </div>
                                                        <div className="text-xs text-neutral-text-secondary mt-2 space-y-1">
                                                            {change.alasan && <div>Alasan: {change.alasan}</div>}
                                                            {change.pengubah && <div>Diputuskan oleh: {change.pengubah}</div>}
                                                            {change.tanggal && <div>Tanggal: {formatDateShort(change.tanggal)}</div>}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        // ========== DEADLINE EXTENDED ==========
                                        if (item.type === 'deadline_extended') {
                                            const ext = item.data;
                                            return (
                                                <div key={`extended-${i}`} className="relative pl-10">
                                                    <div className="absolute left-2.5 w-3 h-3 rounded-full border-2 border-white bg-amber-500" />
                                                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <CalendarPlus className="w-4 h-4 text-amber-600" />
                                                            <span className="text-sm font-semibold text-amber-700">
                                                                Deadline Diperpanjang
                                                            </span>
                                                        </div>
                                                        <div className="text-sm text-amber-800 font-medium">
                                                            {ext.deadline_lama || '-'} → {ext.deadline_baru || '-'}
                                                        </div>
                                                        <div className="text-xs text-neutral-text-secondary mt-2 space-y-1">
                                                            {ext.alasan && <div>Alasan: {ext.alasan}</div>}
                                                            {ext.pengubah && <div>Diputuskan oleh: {ext.pengubah}</div>}
                                                            {ext.tanggal && <div>Tanggal: {formatDateShort(ext.tanggal)}</div>}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        return null;
                                    })
                                ) : (
                                    <div className="pl-10 text-sm text-neutral-text-secondary">
                                        Belum ada riwayat progress.
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                )}

                {/* Riwayat PPEPP Tab */}
                {activeTab === 'riwayat_ppepp' && (
                    <Card>
                        <h3 className="text-base font-bold text-teal-dark mb-4">
                            Riwayat PPEPP
                        </h3>
                        {riwayatPpepp.length === 0 ? (
                            <p className="text-sm text-neutral-text-secondary">
                                Belum ada riwayat PPEPP.
                            </p>
                        ) : (
                            <div className="space-y-4">
                                {riwayatPpepp.map((item, index) => {
                                    const statusRiwayat =
                                        item.id === latestRiwayatPpeppId &&
                                            item.status !== 'dibatalkan'
                                            ? 'Aktif'
                                            : 'Diarsipkan';
                                    const isLatest = index === riwayatPpepp.length - 1;
                                    const isExpanded = isLatest || expandedPpeppIds.has(item.id);
                                    const progressPpepp = Number(item.progress_percentage || 0);

                                    return (
                                        <div
                                            key={item.id}
                                            className="rounded-xl border border-neutral-border bg-neutral-bg/40 overflow-hidden"
                                        >
                                            <button
                                                type="button"
                                                className="w-full flex items-center justify-between p-4 text-left hover:bg-neutral-bg/60 transition-colors"
                                                onClick={() => {
                                                    if (isLatest) return;
                                                    setExpandedPpeppIds((prev) => {
                                                        const next = new Set(prev);
                                                        if (next.has(item.id)) next.delete(item.id);
                                                        else next.add(item.id);
                                                        return next;
                                                    });
                                                }}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <h4 className="text-sm font-bold text-neutral-text-primary">
                                                        PPEPP ke-{index + 1}
                                                    </h4>
                                                    <span
                                                        className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusRiwayat === 'Aktif'
                                                            ? 'bg-blue-100 text-blue-700'
                                                            : 'bg-neutral-bg text-neutral-text-secondary'
                                                            }`}
                                                    >
                                                        {statusRiwayat}
                                                    </span>
                                                    <span className="text-xs text-neutral-text-secondary">
                                                        {progressPpepp}%
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-20 h-1.5 bg-neutral-border rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-teal-main rounded-full transition-all"
                                                            style={{ width: `${progressPpepp}%` }}
                                                        />
                                                    </div>
                                                    {!isLatest && (
                                                        <ChevronDown
                                                            className={`w-4 h-4 text-neutral-text-secondary transition-transform ${isExpanded ? 'rotate-180' : ''
                                                                }`}
                                                        />
                                                    )}
                                                </div>
                                            </button>
                                            {isExpanded && (
                                                <div className="px-4 pb-4 space-y-3">
                                                    <div className="text-xs text-neutral-text-secondary">
                                                        Dibuat: {formatDate(item.created_at)}
                                                        {item.rapat_pimpinan?.judul
                                                            ? ` di ${item.rapat_pimpinan.judul}`
                                                            : ''}
                                                    </div>
                                                    <ProgressBar
                                                        value={progressPpepp}
                                                        size="sm"
                                                        color="teal"
                                                    />
                                                    <div className="space-y-2">
                                                        {[
                                                            {
                                                                label: 'Program',
                                                                value: item.program,
                                                                color: 'border-l-teal-main',
                                                            },
                                                            {
                                                                label: 'Pengendalian',
                                                                value: item.pengendalian,
                                                                color: 'border-l-info',
                                                            },
                                                            {
                                                                label: 'Evaluasi',
                                                                value: item.evaluasi,
                                                                color: 'border-l-warning',
                                                            },
                                                            {
                                                                label: 'Pengembangan',
                                                                value: item.pengembangan,
                                                                color: 'border-l-success',
                                                            },
                                                            {
                                                                label: 'Pelaporan',
                                                                value: item.pelaporan,
                                                                color: 'border-l-purple-500',
                                                            },
                                                        ].map((field, fi) => (
                                                            <div
                                                                key={fi}
                                                                className={`border-l-4 ${field.color} pl-3`}
                                                            >
                                                                <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                                                                    {field.label}
                                                                </p>
                                                                <p className="text-xs text-neutral-text-primary leading-relaxed">
                                                                    {field.value || '-'}
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className="flex items-center gap-2 text-xs text-neutral-text-secondary pt-1">
                                                        <Users className="w-3 h-3" />
                                                        <span>
                                                            PIC: {item.pic_user?.name || item.pic_unit?.name || '-'}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Card>
                )}

                {/* Hambatan Tab */}
                {activeTab === 'hambatan' && (
                    <Card>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                                Hambatan Tindak Lanjut
                            </h3>
                            {hambatanTerbaru && (
                                <span
                                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${hambatanTerbaru.status === 'diperpanjang'
                                        ? 'bg-amber-100 text-amber-700'
                                        : hambatanTerbaru.status === 'dikembalikan_rapim'
                                            ? 'bg-blue-100 text-blue-700'
                                            : hambatanTerbaru.status === 'pic_diganti'
                                                ? 'bg-purple-100 text-purple-700'
                                                : hambatanTerbaru.status === 'selesai'
                                                    ? 'bg-success/10 text-success'
                                                    : 'bg-danger/10 text-danger'
                                        }`}
                                >
                                    {hambatanTerbaru.status === 'diperpanjang'
                                        ? 'DIPERPANJANG'
                                        : hambatanTerbaru.status === 'dikembalikan_rapim'
                                            ? 'DIKEMBALIKAN'
                                            : hambatanTerbaru.status === 'pic_diganti'
                                                ? 'PIC DIGANTI'
                                                : hambatanTerbaru.status === 'selesai'
                                                    ? 'DISELESAIKAN'
                                                    : 'AKTIF'}
                                </span>
                            )}
                        </div>

                        {hambatanOrdered.length === 0 ? (
                            <p className="text-sm text-neutral-text-secondary">
                                Belum ada hambatan yang dilaporkan.
                            </p>
                        ) : (
                            <div className="space-y-4">
                                {hambatanOrdered.map((item) => (
                                    <div
                                        key={item.id}
                                        className="p-4 rounded-lg bg-neutral-bg border border-neutral-border"
                                    >
                                        <div className="flex items-center justify-between text-xs text-neutral-text-secondary mb-2">
                                            <span>{formatDateShort(item.created_at)}</span>
                                            <span>Pelapor: {item.pelapor?.name || '-'}</span>
                                        </div>
                                        <p className="text-sm font-semibold text-danger">
                                            {item.jenis_hambatan}
                                        </p>
                                        <p className="text-sm text-neutral-text-primary mt-1">
                                            {item.deskripsi}
                                        </p>
                                        {item.usulan_solusi && (
                                            <div className="text-xs text-neutral-text-secondary mt-2">
                                                Usulan Solusi:{' '}
                                                <span className="text-neutral-text-primary">
                                                    {item.usulan_solusi}
                                                </span>
                                            </div>
                                        )}
                                        {item.catatan_keputusan && (
                                            <div className="mt-3 p-2 rounded-lg bg-white border border-neutral-border text-xs">
                                                <p className="font-semibold text-neutral-text-primary mb-1">
                                                    Keputusan Pimpinan:
                                                </p>
                                                <p className="text-neutral-text-secondary">
                                                    {item.catatan_keputusan}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Action Buttons for Pimpinan */}
                        {showHambatanActions && (
                            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-neutral-border">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    icon={<Clock className="w-4 h-4" />}
                                    onClick={() => {
                                        setNewDeadline(
                                            data.target_date
                                                ? data.target_date.toString().slice(0, 10)
                                                : ''
                                        );
                                        setAlasanPerpanjang('');
                                        setShowExtendModal(true);
                                    }}
                                    disabled={batasPerpanjanganTercapai}
                                >
                                    Perpanjang Deadline{' '}
                                    {jumlahPerpanjangan > 0 ? `(${jumlahPerpanjangan}x)` : ''}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    icon={<UserCog className="w-4 h-4" />}
                                    onClick={() => {
                                        setSelectedPic('');
                                        setAlasanGantiPic('');
                                        setFilterUnitPic('');
                                        setShowChangePicModal(true);
                                        loadPicCandidates();
                                    }}
                                >
                                    Ganti PIC
                                </Button>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    icon={<RotateCcw className="w-4 h-4" />}
                                    onClick={() => {
                                        setAlasanKembaliRapim('');
                                        setShowReturnModal(true);
                                    }}
                                >
                                    Kembalikan ke RAPIM
                                </Button>
                            </div>
                        )}
                    </Card>
                )}
            </div>

            {/* KPI Section */}
            {kpis.length > 0 && (
                <Card>
                    <h3 className="text-base font-bold text-teal-dark mb-4 flex items-center gap-2">
                        <Target className="w-5 h-5" /> Key Performance Indicators
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {kpis.map((kpi) => (
                            <KpiCard key={kpi.id} kpi={kpi} />
                        ))}
                    </div>
                </Card>
            )}

            {/* ========== MODALS ========== */}

            {/* Add Progress Modal */}
            <Modal
                isOpen={showAddProgress}
                onClose={() => setShowAddProgress(false)}
                title="📊 Update Progress"
                size="md"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowAddProgress(false)}>
                            Batal
                        </Button>
                        <Button variant="primary" onClick={handleAddProgress} loading={saving}>
                            Simpan Progress
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">
                            Persentase Progress <span className="text-danger">*</span>
                        </label>
                        <div className="flex items-center gap-4">
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={newProgress.progress_percentage || 0}
                                onChange={(e) =>
                                    setNewProgress({
                                        ...newProgress,
                                        progress_percentage: e.target.value,
                                    })
                                }
                                className="flex-1"
                            />
                            <span className="text-lg font-black text-teal-main w-14 text-right">
                                {newProgress.progress_percentage || 0}%
                            </span>
                        </div>
                        <ProgressBar
                            value={Number(newProgress.progress_percentage) || 0}
                            size="sm"
                            showLabel={false}
                            color="teal"
                        />
                    </div>

                    <Textarea
                        label="Keterangan"
                        placeholder="Jelaskan progress yang telah dicapai..."
                        value={newProgress.keterangan}
                        onChange={(e) =>
                            setNewProgress({ ...newProgress, keterangan: e.target.value })
                        }
                        required
                        rows={4}
                    />

                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">
                            Bukti (Opsional)
                        </label>
                        <label className="flex items-center gap-3 p-3 border border-dashed border-neutral-border rounded-lg cursor-pointer hover:border-teal-light transition-colors">
                            <Upload className="w-5 h-5 text-neutral-text-secondary" />
                            <div>
                                <p className="text-sm font-medium">
                                    {newProgress.bukti ? newProgress.bukti.name : 'Pilih file bukti'}
                                </p>
                                <p className="text-xs text-neutral-text-secondary">
                                    PDF, DOC, XLS, JPG, PNG (Max 5MB)
                                </p>
                            </div>
                            <input
                                type="file"
                                onChange={(e) => {
                                    const file = e.target.files?.[0] || null;
                                    if (file && file.size > 5 * 1024 * 1024) {
                                        toast.error('Ukuran file maksimal 5MB');
                                        return;
                                    }
                                    setNewProgress({ ...newProgress, bukti: file });
                                }}
                                className="hidden"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                            />
                        </label>
                    </div>
                </div>
            </Modal>

            {/* Laporkan Hambatan Modal */}
            <Modal
                isOpen={showHambatanForm}
                onClose={() => setShowHambatanForm(false)}
                title="Laporkan Hambatan"
                size="md"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowHambatanForm(false)}>
                            Batal
                        </Button>
                        <Button
                            variant="warning"
                            onClick={handleReportHambatan}
                            loading={savingHambatan}
                        >
                            Simpan Hambatan
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Select
                        label="Jenis Hambatan"
                        placeholder="Pilih jenis hambatan"
                        value={hambatanForm.jenis_hambatan}
                        onChange={(value) =>
                            setHambatanForm({ ...hambatanForm, jenis_hambatan: value })
                        }
                        options={JENIS_HAMBATAN_OPTIONS}
                        required
                    />
                    <Textarea
                        label="Deskripsi Hambatan"
                        placeholder="Jelaskan hambatan yang terjadi..."
                        value={hambatanForm.deskripsi}
                        onChange={(e) =>
                            setHambatanForm({ ...hambatanForm, deskripsi: e.target.value })
                        }
                        required
                        rows={4}
                    />
                    <Textarea
                        label="Usulan Solusi (Opsional)"
                        placeholder="Tuliskan usulan solusi atau alternatif..."
                        value={hambatanForm.usulan_solusi}
                        onChange={(e) =>
                            setHambatanForm({ ...hambatanForm, usulan_solusi: e.target.value })
                        }
                        rows={3}
                    />
                    <div className="text-xs text-neutral-text-secondary">
                        Hambatan dapat dilaporkan jika target sudah lewat atau dalam 14 hari
                        ke depan.
                    </div>
                </div>
            </Modal>

            {/* Perpanjang Deadline Modal */}
            <Modal
                isOpen={showExtendModal}
                onClose={() => setShowExtendModal(false)}
                title="Perpanjang Deadline"
                size="sm"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowExtendModal(false)}>
                            Batal
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handlePerpanjangDeadline}
                            loading={actionLoading}
                        >
                            Simpan
                        </Button>
                    </>
                }
            >
                <div className="space-y-3">
                    <Input
                        label="Tanggal Target Baru"
                        type="date"
                        value={newDeadline}
                        onChange={(e) => setNewDeadline(e.target.value)}
                        required
                    />
                    <Textarea
                        label="Alasan Perpanjangan"
                        placeholder="Tuliskan alasan perpanjangan..."
                        value={alasanPerpanjang}
                        onChange={(e) => setAlasanPerpanjang(e.target.value)}
                        required
                        rows={3}
                    />
                    <p className="text-xs text-neutral-text-secondary">
                        Tanggal baru akan menggantikan target sebelumnya.
                    </p>
                    {jumlahPerpanjangan > 0 && (
                        <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                            Perpanjangan sebelumnya: {jumlahPerpanjangan}x (maksimal 2x)
                        </p>
                    )}
                </div>
            </Modal>

            {/* Ganti PIC Modal */}
            <Modal
                isOpen={showChangePicModal}
                onClose={() => setShowChangePicModal(false)}
                title="Ganti PIC"
                size="sm"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowChangePicModal(false)}>
                            Batal
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleGantiPic}
                            loading={actionLoading}
                        >
                            Simpan
                        </Button>
                    </>
                }
            >
                <div className="space-y-3">
                    <Select
                        label="Filter Unit (opsional)"
                        placeholder="Semua Unit"
                        value={filterUnitPic}
                        onChange={(value) => {
                            setFilterUnitPic(value);
                            setSelectedPic('');
                        }}
                        options={[
                            { value: '', label: 'Semua Unit' },
                            ...Array.from(
                                new Map(
                                    picCandidates
                                        .filter((u) => u.unit)
                                        .map((u) => [u.unit!.id, u.unit!])
                                ).values()
                            )
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((unit) => ({ value: unit.id, label: unit.name })),
                        ]}
                        disabled={loadingPicCandidates}
                    />
                    <Select
                        label="PIC Baru"
                        placeholder={loadingPicCandidates ? 'Memuat kandidat...' : 'Pilih PIC'}
                        value={selectedPic}
                        onChange={(value) => setSelectedPic(value)}
                        options={picCandidates
                            .filter((u) => !filterUnitPic || u.unit_id === Number(filterUnitPic))
                            .map((u) => ({
                                value: u.id,
                                label: `${u.name}${u.unit?.name ? ` — ${u.unit.name}` : ''}`,
                            }))}
                        disabled={loadingPicCandidates}
                        required
                    />
                    <Textarea
                        label="Alasan Pergantian"
                        placeholder="Tuliskan alasan pergantian PIC..."
                        value={alasanGantiPic}
                        onChange={(e) => setAlasanGantiPic(e.target.value)}
                        required
                        rows={3}
                    />
                    <p className="text-xs text-neutral-text-secondary">
                        PIC baru akan menggantikan PIC sebelumnya. Progres tidak akan
                        di-reset.
                    </p>
                </div>
            </Modal>

            {/* Kembalikan ke RAPIM Modal */}
            <Modal
                isOpen={showReturnModal}
                onClose={() => setShowReturnModal(false)}
                title="Kembalikan ke RAPIM"
                size="sm"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setShowReturnModal(false)}>
                            Batal
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleKembalikanRapim}
                            loading={actionLoading}
                        >
                            Kembalikan
                        </Button>
                    </>
                }
            >
                <div className="space-y-3">
                    <p className="text-sm text-neutral-text-secondary">
                        Laporan akan dikembalikan ke antrean RAPIM untuk dibahas ulang.
                    </p>
                    <Textarea
                        label="Alasan Pengembalian"
                        placeholder="Tuliskan alasan pengembalian ke RAPIM..."
                        value={alasanKembaliRapim}
                        onChange={(e) => setAlasanKembaliRapim(e.target.value)}
                        required
                        rows={3}
                    />
                    <p className="text-xs text-danger">
                        PPEPP saat ini akan diarsipkan dan PPEPP baru akan dibuat setelah
                        RAPIM ulang.
                    </p>
                </div>
            </Modal>

            {/* Tolak Konfirmasi Selesai Modal */}
            <Modal
                isOpen={showTolakSelesaiModal}
                onClose={() => setShowTolakSelesaiModal(false)}
                title="Tolak Konfirmasi Selesai"
                size="sm"
                footer={
                    <>
                        <Button
                            variant="outline"
                            onClick={() => setShowTolakSelesaiModal(false)}
                        >
                            Batal
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleTolakSelesai}
                            loading={actionLoading}
                        >
                            Tolak Konfirmasi
                        </Button>
                    </>
                }
            >
                <div className="space-y-3">
                    <Textarea
                        label="Alasan Penolakan"
                        placeholder="Jelaskan alasan pekerjaan belum dapat diterima sebagai selesai..."
                        value={alasanTolakSelesai}
                        onChange={(e) => setAlasanTolakSelesai(e.target.value)}
                        required
                        rows={4}
                    />
                    <p className="text-xs text-danger">
                        Status PPEPP akan dikembalikan ke Dalam Proses dan PIC akan diminta
                        memperbaiki pekerjaannya.
                    </p>
                </div>
            </Modal>
        </div>
    );
};

export default TindakLanjutDetail;
